import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { FiChevronRight, FiShoppingCart } from 'react-icons/fi';
import { pageTransition, staggerContainer, staggerItem } from '../helpers/animations';
import { api, normalizeToken, tokenPath } from '../helpers';
import { API_BASE_URL } from '../helpers/config';
import { MerchantListSkeleton } from '../components/skeletons';

function normalizeChannels(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => ({
      upi_id: String(item?.upi_id || '').trim(),
      min: Number(item?.min),
      max: Number(item?.max),
      provider: String(item?.provider || '').trim(),
      mid: String(item?.mid ?? item?.merchant_id ?? '').trim(),
    }))
    .filter((item) => item.upi_id);
}

function buildMerchantGroups(channels) {
  const hasMid = channels.some((c) => c.mid);
  if (hasMid) {
    const map = new Map();
    for (const ch of channels) {
      if (!ch.mid) continue;
      if (!map.has(ch.mid)) {
        map.set(ch.mid, { mid: ch.mid, label: ch.provider || 'Marchent', channels: [] });
      }
      map.get(ch.mid).channels.push(ch);
    }
    return Array.from(map.values());
  }

  const byProv = new Map();
  for (const ch of channels) {
    const key = ch.provider || 'Marchent';
    if (!byProv.has(key)) {
      byProv.set(key, { mid: key, label: key, channels: [] });
    }
    byProv.get(key).channels.push(ch);
  }
  return Array.from(byProv.values());
}

function formatRange(channels) {
  const mins = channels.map((c) => c.min).filter(Number.isFinite);
  const maxs = channels.map((c) => c.max).filter(Number.isFinite);
  if (mins.length === 0 || maxs.length === 0) return 'Limits vary by Marchent';
  const lo = Math.min(...mins);
  const hi = Math.max(...maxs);
  return `\u20B9${lo} – \u20B9${hi}`;
}

function BuyMerchants() {
  const { token: tokenParam } = useParams();
  const token = normalizeToken(tokenParam);
  const [loading, setLoading] = useState(false);
  const [channels, setChannels] = useState([]);

  useEffect(() => {
    const run = async () => {
      if (!token) return;
      setLoading(true);
      try {
        const { data } = await api.post(`${API_BASE_URL}/exchange/exchanger-upi-id`, { token, mid: '' });
        if (data?.error === false && Array.isArray(data?.data)) {
          setChannels(normalizeChannels(data.data));
        } else if (data?.error === 'session expired') {
          toast.error('Session expired. Please open your access link again.');
        } else if (data?.error) {
          toast.error(data.error);
        }
      } catch {
        // api helper
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [token]);

  const merchants = useMemo(() => buildMerchantGroups(channels), [channels]);

  return (
    <motion.div
      {...pageTransition}
      transition={{ duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] }}
      style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}
    >
      <motion.h1 className="screen-title" variants={staggerItem} initial="initial" animate="animate">
        Buy USDT
      </motion.h1>
      <motion.p
        className="buy-merchant-back"
        variants={staggerItem}
        initial="initial"
        animate="animate"
      >
        <Link to={tokenPath(token, 'trade')}>Back to Trade</Link>
      </motion.p>

      <motion.div
        className="card"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05, duration: 0.25 }}
      >
        <h2 className="card-title">Choose Marchent</h2>
        <p className="card-subtitle">Select a Marchent to see payment details and complete your purchase.</p>

        {loading && <MerchantListSkeleton />}

        {!loading && merchants.length === 0 && (
          <div className="merchant-pick-empty">No Marchents available right now.</div>
        )}

        {!loading && merchants.length > 0 && (
          <motion.div
            className="merchant-pick-list"
            variants={staggerContainer}
            initial="initial"
            animate="animate"
          >
            {merchants.map((m) => (
              <motion.div key={m.mid} variants={staggerItem}>
                <Link to={`${tokenPath(token, 'buy', 'pay')}?mid=${encodeURIComponent(m.mid)}`} className="merchant-pick-card">
                  <div className="merchant-pick-icon" aria-hidden>
                    <FiShoppingCart size={18} />
                  </div>
                  <div className="merchant-pick-body">
                    <span className="merchant-pick-name">{m.label}</span>
                    <span className="merchant-pick-meta">
                      {m.channels.length} Marchent{m.channels.length !== 1 ? 's' : ''} · {formatRange(m.channels)}
                    </span>
                  </div>
                  <FiChevronRight className="merchant-pick-chevron" size={20} />
                </Link>
              </motion.div>
            ))}
          </motion.div>
        )}
      </motion.div>
    </motion.div>
  );
}

export default BuyMerchants;
